// chat/run — chat's interpreter: the module's ONE impure file. Performs each
// ChatEffect against the ports and feeds every outcome back as a ChatCommand
// through `dispatch`. Each effect is one port chain; anything that depends on
// its outcome is decided afresh by `updateChat` (a nascent first send is
// `start_conversation`, then `flush_grounding`, then the held `send_turn`).

import * as Core from "@bilda/core";
import { exhaustSwitchCase, Result } from "@bilda/utils";
import { clearFormDraft } from "../../app/formDraftStore.js";
import type { AppPorts } from "../../app/ports.js";
import type { Interpreter } from "../module.js";
import type { ChatCommand } from "./command.js";
import type {
    ChatEffect,
    FlushGroundingEffect,
    FollowTurnEffect,
    SendTurnEffect,
    SupplyInformationEffect,
} from "./effect.js";

type ChatPorts = Pick<
    AppPorts,
    | "sendTurn"
    | "interruptTurn"
    | "offerOpeningMove"
    | "loadConversation"
    | "followTurn"
    | "situationProfileView"
    | "startConversation"
    | "introduceToContext"
    | "groundConversation"
    | "supplyInformation"
    | "acceptProposal"
    | "rejectProposal"
>;

export type ChatDeps = {
    readonly ports: ChatPorts;
    // Mints the id of a client-only system row (the connection-loss row).
    readonly mkLocalSystemId: () => string;
};

type Dispatch = (command: ChatCommand) => void;

export const mkRunChat = ({
    ports,
    mkLocalSystemId,
}: ChatDeps): Interpreter<ChatEffect, ChatCommand> => {
    const sendTurn = async (
        {
            conversationId,
            target,
            text,
            anchor,
            mentions,
            ids,
            resumesRequestId,
        }: SendTurnEffect,
        dispatch: Dispatch,
    ): Promise<void> => {
        const r = await ports.sendTurn(
            conversationId,
            target,
            text,
            // D4: the @-reference stamp, mapped to the wire `resourceRefs`
            // channel so the turn's prompt carries inspect/act pointers.
            mentions,
            anchor,
            // Each live stream event re-enters as `turn_progressed`.
            (event) =>
                dispatch({
                    type: "turn_progressed",
                    conversationId,
                    event,
                    ids,
                }),
            resumesRequestId,
        );
        if (Result.isOk(r)) {
            const { reply, landed } = r.value;
            dispatch({
                type: "turn_replied",
                conversationId,
                reply,
                ids,
                ...(landed !== undefined && target !== null
                    ? { proposal: { target, landed } }
                    : {}),
            });
            return;
        }
        dispatch({
            type: "turn_refused",
            conversationId,
            error: r.error,
            entryId: mkLocalSystemId(),
        });
    };

    const startConversation = async (
        conversationId: string,
        dispatch: Dispatch,
    ): Promise<void> => {
        const r = await ports.startConversation(undefined, conversationId);
        dispatch(
            Result.isOk(r)
                ? {
                      type: "conversation_started",
                      conversationId,
                      summary: r.value,
                  }
                : { type: "conversation_start_failed", conversationId },
        );
    };

    // Sequential (each introduce is its own command) so the causal order
    // matches the pin order. Then the buffered audience: ground it and refetch
    // the chip's projection.
    const flushGrounding = async (
        { conversationId, grounding }: FlushGroundingEffect,
        dispatch: Dispatch,
    ): Promise<void> => {
        const anchor = Core.Conversation.Ref.mk(
            Core.Conversation.Id.mk(conversationId),
            Core.Versioning.Version.v1,
        );
        for (const ref of grounding.sources)
            await ports.introduceToContext(
                anchor,
                Core.Resource.Ref.mkWhole(
                    Core.Resource.Id.mk(ref.id),
                    Core.Versioning.Version.v1,
                ),
            );
        const { situationContextId } = grounding;
        if (
            situationContextId !== undefined &&
            Result.isOk(
                await ports.groundConversation(
                    conversationId,
                    situationContextId,
                ),
            )
        ) {
            const view = await ports.situationProfileView(conversationId);
            if (Result.isOk(view))
                dispatch({
                    type: "conversation_grounded",
                    conversationId,
                    profile: view.value,
                });
        }
        dispatch({ type: "pending_grounding_flushed", conversationId });
    };

    const supplyInformation = async (
        { conversationId, answer, resume }: SupplyInformationEffect,
        dispatch: Dispatch,
    ): Promise<void> => {
        const r = await ports.supplyInformation(
            conversationId,
            resume.requestId,
            answer,
        );
        if (Result.isErr(r)) {
            dispatch({
                type: "information_supply_failed",
                conversationId,
                error: r.error,
            });
            return;
        }
        // The answer is in the event log now: drop the device-local draft.
        // Only here, never on click, so a failed round-trip keeps the author's
        // answers for the retry (or a reload).
        clearFormDraft(resume.requestId);
        // Re-read so the folded `information_answer` collapses the form to its
        // answered summary before the resume streams.
        await reloadTranscript(conversationId, dispatch);
        dispatch({ type: "information_supplied", conversationId, resume });
    };

    // Accepting or discarding a proposal: report the outcome as its command.
    const settleProposal = async (
        call: ReturnType<ChatPorts["acceptProposal"]>,
        settled: ChatCommand,
        conversationId: string,
        dispatch: Dispatch,
    ): Promise<void> => {
        const r = await call;
        dispatch(
            Result.isOk(r)
                ? settled
                : { type: "proposal_settle_failed", conversationId },
        );
    };

    // A failed re-read keeps the live transcript; the next load converges it.
    const reloadTranscript = async (
        conversationId: string,
        dispatch: Dispatch,
    ): Promise<void> => {
        const r = await ports.loadConversation(conversationId);
        if (Result.isOk(r))
            dispatch({
                type: "conversation_transcript_loaded",
                conversationId,
                entries: r.value.entries,
            });
    };

    // Two distinct facts, each fed only when its load succeeded: a partial
    // failure keeps the other field. Only a failed transcript re-arms the load.
    const loadConversation = async (
        conversationId: string,
        dispatch: Dispatch,
    ): Promise<void> => {
        const [transcript, profile] = await Promise.all([
            ports.loadConversation(conversationId),
            ports.situationProfileView(conversationId),
        ]);
        dispatch(
            Result.isOk(transcript)
                ? {
                      type: "conversation_loaded",
                      conversationId,
                      entries: transcript.value.entries,
                      turnRunning: transcript.value.turnRunning,
                  }
                : { type: "conversation_load_failed", conversationId },
        );
        if (Result.isOk(profile))
            dispatch({
                type: "conversation_profile_loaded",
                conversationId,
                profile: profile.value,
            });
    };

    // Follow a turn this page did not start until the server says it ended
    // (#100). A stream the browser gave up on ends the follow too: the
    // reload that follows shows whatever the log holds.
    const followTurn = async (
        { conversationId, ids }: FollowTurnEffect,
        dispatch: Dispatch,
    ): Promise<void> => {
        await ports.followTurn(conversationId, (event) =>
            dispatch({ type: "turn_progressed", conversationId, event, ids }),
        );
        dispatch({ type: "followed_turn_ended", conversationId, ids });
    };

    const offerOpeningMove = async (
        conversationId: string,
        dispatch: Dispatch,
    ): Promise<void> => {
        const r = await ports.offerOpeningMove(conversationId);
        if (Result.isErr(r)) {
            dispatch({
                type: "opening_move_failed",
                conversationId,
                error: r.error,
            });
            return;
        }
        dispatch(
            r.value.type === "offered"
                ? {
                      type: "opening_move_offered",
                      conversationId,
                      reply: r.value.reply,
                  }
                : { type: "opening_move_declined", conversationId },
        );
    };

    return (effect, dispatch) => {
        switch (effect.type) {
            case "send_turn":
                void sendTurn(effect, dispatch);
                return;
            case "interrupt_turn":
                // Fire-and-forget (sendTurn cancels the in-flight agent call and
                // discards its answer); a refusal is swallowed: Stop failing
                // quietly beats Stop crashing the composer.
                void ports.interruptTurn(effect.conversationId);
                return;
            case "offer_opening_move":
                void offerOpeningMove(effect.conversationId, dispatch);
                return;
            case "reload_transcript":
                void reloadTranscript(effect.conversationId, dispatch);
                return;
            case "start_conversation":
                void startConversation(effect.conversationId, dispatch);
                return;
            case "flush_grounding":
                void flushGrounding(effect, dispatch);
                return;
            case "supply_information":
                void supplyInformation(effect, dispatch);
                return;
            case "accept_proposal":
                void settleProposal(
                    ports.acceptProposal(effect.proposalId),
                    {
                        type: "proposal_accepted",
                        conversationId: effect.conversationId,
                    },
                    effect.conversationId,
                    dispatch,
                );
                return;
            case "reject_proposal":
                void settleProposal(
                    ports.rejectProposal(effect.proposalId),
                    {
                        type: "proposal_discarded",
                        conversationId: effect.conversationId,
                    },
                    effect.conversationId,
                    dispatch,
                );
                return;
            case "load_conversation":
                void loadConversation(effect.conversationId, dispatch);
                return;
            case "follow_turn":
                void followTurn(effect, dispatch);
                return;
            default:
                return exhaustSwitchCase(effect);
        }
    };
};
